'use client'

import { useMemo } from 'react'

import CriteriaOverview, { type CriterionWithRating } from '@/components/ui/CriteriaOverview'

import type { App, Category, CategoryCriteriaCriterion } from '@/payload-types'
import { extractUniqueCriteriaFromApp } from '@/lib/formatting'
import { useBreakpoint } from '@/hooks/useBreakpoint'

type EfficiencyPointsProps = {
  app: Pick<
    App,
    | 'id'
    | 'icon'
    | 'slug'
    | 'glyph'
    | 'categoryRecommendations'
    | 'brandBackgroundColor'
    | 'brandPrimaryColor'
    | 'brandSecondaryColor'
    | 'brandTertiaryColor'
    | 'websiteUrl'
    | 'name'
    | 'screenshots'
    | 'homepageImage'
  >
  appCategoryEfficiencyPoints?: number | null
  criteria?: CriterionWithRating[]
  categoryId?: Category['id']
  reviewCriterionRatings?: {
    categoryCriterion?: number | CategoryCriteriaCriterion | null
    rating?: number | null
  }[]
  align: 'left' | 'right'
  inline?: boolean
  loading?: boolean
  alwaysShow?: boolean
}

export default function EfficiencyPoints({
  app,
  criteria,
  appCategoryEfficiencyPoints,
  categoryId,
  reviewCriterionRatings,
  align,
  inline,
  loading,
  alwaysShow,
}: EfficiencyPointsProps) {
  const { lg } = useBreakpoint()

  const resolvedCriteria = useMemo(
    () => criteria ?? extractUniqueCriteriaFromApp(app, reviewCriterionRatings, categoryId),
    [criteria, app, reviewCriterionRatings, categoryId],
  )

  const allCriteriaRated =
    !loading &&
    resolvedCriteria.length > 0 &&
    resolvedCriteria.every(
      (c) => c.isApplicable === false || (c.rating !== null && c.rating !== undefined),
    )

  if (!allCriteriaRated && !alwaysShow) return null

  const Wrapper = inline ? 'span' : 'div'

  return (
    <Wrapper className={!inline ? 'hidden sm:flex' : 'inline-flex'}>
      <CriteriaOverview
        app={app}
        criteria={resolvedCriteria}
        horizontalAlign={inline ? 'center' : align}
        verticalAlign={inline ? 'top' : lg ? 'center' : 'bottom'}
        hideDescription={true}
      >
        {inline ? (
          <span className="dotted-underline inline-flex cursor-help items-center gap-1">
            <span className="inline-flex min-w-3 justify-center">
              {loading ? (
                <span className="inline-block h-4 w-4 animate-pulse rounded bg-white/20" />
              ) : (
                appCategoryEfficiencyPoints ?? '-'
              )}
            </span>
            <span>efficiency points</span>
          </span>
        ) : (
          <span className="flex min-w-[72px] cursor-default justify-center rounded-xl px-3 py-0.5 font-heading text-36 font-bold transition-colors hover:bg-white/10 md:text-48 lg:text-64">
            {loading ? (
              <span className="inline-block h-8 w-8 animate-pulse rounded-lg bg-white/20 md:h-10 md:w-10 lg:h-12 lg:w-12" />
            ) : (
              appCategoryEfficiencyPoints ?? '-'
            )}
          </span>
        )}
      </CriteriaOverview>
    </Wrapper>
  )
}
